import { ConfirmDialog, plural, titleCase } from '@westhetics/ui'
import type { AnamnesisDraft } from './api'
import { draftSectionPosition } from './labels'

/**
 * Cancelar uma anamnese não preenchida (RN-ANA-05): o modal diz o efeito real (o link deixa de
 * valer, o rascunho é apagado), o que não se perde (o cadastro) e oferece a alternativa (reenviar).
 * Cancelar existe; excluir, não.
 */

export interface CancelTarget {
  id: string
  patientName: string
  /** `undefined` quando a tela não sabe se a paciente já começou a responder. */
  draft?: AnamnesisDraft | null
}

/** O efeito real, com o que a paciente já respondeu quando a tela sabe. */
function cancelEffect(draft: AnamnesisDraft | null | undefined) {
  const kept = 'Nada do cadastro se perde.'
  if (draft === null) {
    return `O link deixa de funcionar. Ela ainda não respondeu nada. ${kept}`
  }
  if (draft === undefined) {
    return `O link deixa de funcionar e o que ela já tiver respondido é apagado. ${kept}`
  }
  const position = draftSectionPosition(draft)
  const answered = position && position > 1 ? ` (${plural(position - 1, 'bloco')})` : ''
  return `O link deixa de funcionar e o que ela já respondeu${answered} é apagado. ${kept}`
}

export function CancelAnamnesisDialog({
  target,
  confirmLabel = 'Cancelar anamnese',
  loading,
  onClose,
  onConfirm,
  onResend,
}: {
  /** A anamnese a cancelar; `null` fecha o modal. */
  target: CancelTarget | null
  confirmLabel?: string
  loading?: boolean
  onClose: () => void
  onConfirm: (target: CancelTarget) => void
  /** A alternativa menos destrutiva: reenviar o link. */
  onResend?: (target: CancelTarget) => void
}) {
  return (
    <ConfirmDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Cancelar a anamnese de ${target ? titleCase(target.patientName) : ''}?`}
      effect={cancelEffect(target?.draft)}
      alternative={
        onResend
          ? {
              description:
                'Alternativa: reenviar o link. A paciente recebe um link novo e continua de onde parou.',
              label: 'Reenviar',
              onSelect: () => target && onResend(target),
            }
          : undefined
      }
      cancelLabel="Voltar"
      confirmLabel={confirmLabel}
      loading={loading}
      loadingLabel="Cancelando…"
      onConfirm={() => target && onConfirm(target)}
    />
  )
}
